function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { useEffect, useRef } from 'react';
import { classnames } from '@bydesign-ui/utils';
import { getElementPosition, getElementScroll, useScrollTo } from '@bydesign-ui/hooks';
import { usePrefixCls, useDirection } from '@byte-design/config';
import { Right } from '../../../common/icons';
import { useCascader, useCascaderActive } from '../../../hooks/cascader';
import { AutoSizedList } from '../../../units/auto-sized-list';
import { Loading } from '../../loading';
import { CascaderItem } from './CascaderItem';
import { CascaderItemAll } from './CascaderItemAll';
export function CascaderColumn(props) {
  var parentId = props.parentId,
      className = props.className,
      columnTitle = props.columnTitle,
      renderItem = props.renderItem,
      columnWidth = props.columnWidth,
      children = props.children,
      _props$rowHeight = props.rowHeight,
      rowHeight = _props$rowHeight === void 0 ? 34 : _props$rowHeight,
      _props$listHeight = props.listHeight,
      listHeight = _props$listHeight === void 0 ? 192 : _props$listHeight,
      extra = _objectWithoutProperties(props, ["parentId", "className", "columnTitle", "renderItem", "columnWidth", "children", "rowHeight", "listHeight"]);

  var direction = useDirection();
  var boxRef = useRef(null);
  var cascader = useCascader();
  var colWidth = columnWidth || cascader.columnWidth;
  var info = cascader.getItemInfo(parentId) || {};
  var expandedIds = useCascaderActive();
  var cls = classnames(usePrefixCls('cascader-column', {
    rtl: direction === 'rtl'
  }), className);
  var listCls = usePrefixCls('cascader-column-list');
  var innerCls = usePrefixCls('cascader-column-inner', {
    loading: info.loading
  });
  var activeCls = usePrefixCls('cascader-active-item');
  var titleCls = usePrefixCls('cascader-column-title');
  var arrowCls = usePrefixCls('cascader-item-arrow');
  var labelCls = usePrefixCls('cascader-item-label');
  var loadingCls = usePrefixCls('cascader-column-loading');

  var renderTitle = function renderTitle() {
    if (!columnTitle) {
      return null;
    }

    return React.createElement("div", {
      className: titleCls
    }, columnTitle);
  };

  var renderDefaultItem = function renderDefaultItem(item) {
    var label = item.label,
        itemChildren = item.children,
        needLoad = item.needLoad;
    var hasArrow = needLoad || !!itemChildren.length;
    return React.createElement(React.Fragment, null, React.createElement("div", {
      className: labelCls
    }, label), hasArrow && React.createElement(Right, {
      className: arrowCls
    }));
  };

  var renderCustomItem = function renderCustomItem(item) {
    var itemChildren = item.children,
        needLoad = item.needLoad;
    var hasArrow = needLoad || !!itemChildren.length;
    return React.createElement(React.Fragment, null, renderItem && React.createElement("div", {
      className: labelCls
    }, renderItem(item)), hasArrow && React.createElement(Right, {
      className: arrowCls
    }));
  };

  var renderCascaderListItem = function renderCascaderListItem(_ref) {
    var key = _ref.key,
        index = _ref.index,
        style = _ref.style;

    if (info.showAll) {
      if (!index) {
        return React.createElement(CascaderItemAll, {
          style: style,
          key: key,
          parentId: parentId
        });
      }

      index = index - 1;
    }

    if (cascader.renderLoading && cascader.renderLoading(cascader.getItemInfo(info.id)) && index === info.children.length) {
      return React.createElement("div", {
        style: style,
        key: key,
        className: loadingCls
      }, cascader.renderLoading(cascader.getItemInfo(info.id)));
    }

    var item = info.children[index];
    var itemCls = '';

    if (expandedIds.indexOf(item.id) > -1) {
      itemCls = activeCls;
    }

    return React.createElement(CascaderItem, {
      key: key,
      id: item.id,
      className: itemCls,
      style: style
    }, renderItem ? renderCustomItem(item) : renderDefaultItem(item));
  };

  var onScroll = function onScroll(e) {
    if (cascader.loadMore && e.clientHeight + e.scrollTop >= e.scrollHeight) {
      var _cascader$getItemInfo = cascader.getItemInfo(info.id),
          path = _cascader$getItemInfo.path,
          level = _cascader$getItemInfo.level,
          _parentId = _cascader$getItemInfo.parentId,
          _children = _cascader$getItemInfo.children;

      cascader.loadMore({
        path: path,
        level: level,
        parent: _parentId ? cascader.getItemInfo(_parentId) : null,
        siblings: _children
      });
      cascader.setActiveId(info.id);
    }
  };

  var renderCascaderList = function renderCascaderList() {
    if (info.loading) {
      return React.createElement("div", {
        className: innerCls
      }, React.createElement(Loading.BlockLoading, {
        show: true,
        maskStyle: {
          background: 'none'
        }
      }));
    }

    if (!info.children || !info.children.length) {
      return null;
    }

    var rowCount = info.children.length + (info.showAll ? 1 : 0) + (cascader.renderLoading && cascader.renderLoading(cascader.getItemInfo(info.id)) ? 1 : 0);
    return React.createElement(AutoSizedList, {
      listClsName: innerCls,
      rowHeight: rowHeight,
      rowCount: rowCount,
      rowRenderer: renderCascaderListItem,
      onScroll: onScroll
    });
  };

  var _useScrollTo = useScrollTo(boxRef, 'top', function () {
    var container = boxRef.current;

    if (!container) {
      return 0;
    }

    var activeELem = container.querySelector(".".concat(activeCls));

    if (!activeELem) {
      return 0;
    }

    var elemPos = getElementPosition(activeELem);
    var containerPos = getElementPosition(container);
    var scrollTop = getElementScroll(container).scrollTop;

    if (elemPos.top >= containerPos.top && elemPos.bottom <= containerPos.bottom) {
      return scrollTop;
    }

    return elemPos.top - containerPos.top + scrollTop;
  }, 150, function () {
    return !!boxRef.current && !!boxRef.current.querySelector(".".concat(activeCls));
  }),
      startScroll = _useScrollTo.startScroll;

  useEffect(function () {
    startScroll();
  });
  return React.createElement("div", Object.assign({}, extra, {
    className: cls,
    style: {
      width: "".concat(colWidth, "px"),
      minWidth: "".concat(colWidth, "px")
    }
  }), renderTitle(), React.createElement("div", {
    className: listCls,
    style: {
      height: listHeight
    },
    ref: boxRef
  }, children, renderCascaderList()));
}